/**
 * Toast Notification Component
 *
 * Provides toast notifications for user feedback.
 * Features:
 * - Multiple toast types (success, error, warning, info)
 * - Auto-dismiss with configurable duration
 * - Manual dismiss with close button
 * - Queue management for multiple toasts
 * - Action buttons (e.g., retry, undo)
 * - Accessible with ARIA live regions
 * - Animations for enter/exit
 */

import { renderIcon, type IconName } from '../utils/icons';

/**
 * Toast type variants
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'default';

/**
 * Toast position on screen
 */
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center';

/**
 * Toast action button
 */
export interface ToastAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void | Promise<void>;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'destructive';
}

/**
 * Individual toast options
 */
export interface ToastOptions {
  /** Toast message */
  message: string;
  /** Toast type (default: 'default') */
  type?: ToastType;
  /** Optional title */
  title?: string;
  /** Duration in ms before auto-dismiss (0 for no auto-dismiss) */
  duration?: number;
  /** Whether to show close button (default: true) */
  dismissible?: boolean;
  /** Optional action button */
  action?: ToastAction;
  /** Custom icon name */
  icon?: IconName;
  /** Callback when toast is dismissed */
  onDismiss?: () => void;
}

/**
 * Toast container options
 */
export interface ToastContainerOptions {
  /** Position on screen (default: 'bottom-right') */
  position?: ToastPosition;
  /** Maximum number of toasts to show at once (default: 5) */
  maxToasts?: number;
  /** Default duration for toasts in ms (default: 5000) */
  defaultDuration?: number;
}

/**
 * Toast configuration by type
 */
const TOAST_CONFIG: Record<ToastType, { icon: IconName; bgClass: string; iconClass: string }> = {
  success: {
    icon: 'checkCircle',
    bgClass: 'bg-success/10 border-success/20',
    iconClass: 'text-success',
  },
  error: {
    icon: 'xCircle',
    bgClass: 'bg-destructive/10 border-destructive/20',
    iconClass: 'text-destructive',
  },
  warning: {
    icon: 'alertTriangle',
    bgClass: 'bg-warning/10 border-warning/20',
    iconClass: 'text-warning',
  },
  info: {
    icon: 'info',
    bgClass: 'bg-info/10 border-info/20',
    iconClass: 'text-info',
  },
  default: {
    icon: 'bell',
    bgClass: 'bg-muted/50 border-border',
    iconClass: 'text-foreground',
  },
};

/**
 * Position CSS classes
 */
const POSITION_CLASSES: Record<ToastPosition, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

/**
 * Toast ID counter
 */
let toastIdCounter = 0;

/**
 * Generate unique toast ID
 */
function generateToastId(): string {
  return `toast-${++toastIdCounter}`;
}

/**
 * Create a single toast element
 */
function createToastElement(id: string, options: ToastOptions, onDismiss: () => void): HTMLElement {
  const {
    message,
    type = 'default',
    title,
    dismissible = true,
    action,
    icon,
  } = options;

  const config = TOAST_CONFIG[type];
  const iconName = icon || config.icon;

  const toast = document.createElement('div');
  toast.id = id;
  toast.className = `toast flex items-start gap-3 w-full max-w-sm p-4 border rounded-xl shadow-lg backdrop-blur-sm ${config.bgClass}`;
  toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
  toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
  toast.setAttribute('data-testid', 'toast');
  toast.setAttribute('data-toast-type', type);
  toast.setAttribute('data-toast-message', message);
  toast.style.cssText = `
    opacity: 0;
    transform: translateX(100%);
    transition: all 0.3s ease-out;
  `;

  // Icon
  const iconWrapper = document.createElement('div');
  iconWrapper.className = `shrink-0 ${config.iconClass}`;
  iconWrapper.appendChild(renderIcon(iconName, { size: 'md' }));
  toast.appendChild(iconWrapper);

  // Content
  const content = document.createElement('div');
  content.className = 'flex-1 min-w-0';

  if (title) {
    const titleElement = document.createElement('p');
    titleElement.className = 'font-semibold text-foreground mb-1';
    titleElement.textContent = title;
    content.appendChild(titleElement);
  }

  const messageElement = document.createElement('p');
  messageElement.className = `text-sm ${title ? 'text-muted-foreground' : 'text-foreground'}`;
  messageElement.textContent = message;
  content.appendChild(messageElement);

  // Action button
  if (action) {
    const actionBtn = document.createElement('button');
    actionBtn.type = 'button';
    const variantClasses = {
      primary: 'text-primary hover:text-primary/80',
      secondary: 'text-foreground hover:text-foreground/80',
      destructive: 'text-destructive hover:text-destructive/80',
    };
    actionBtn.className = `mt-2 text-sm font-medium ${variantClasses[action.variant || 'primary']} transition-colors`;
    actionBtn.textContent = action.label;
    actionBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      await action.onClick();
      onDismiss();
    });
    content.appendChild(actionBtn);
  }

  toast.appendChild(content);

  // Close button
  if (dismissible) {
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'shrink-0 text-muted-foreground hover:text-foreground transition-colors';
    closeBtn.setAttribute('aria-label', 'Dismiss notification');
    closeBtn.appendChild(renderIcon('x', { size: 'sm' }));
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onDismiss();
    });
    toast.appendChild(closeBtn);
  }

  return toast;
}

/**
 * Toast Container class - manages all toasts on the page
 */
export class ToastContainer {
  private container: HTMLElement;
  private options: Required<ToastContainerOptions>;
  private toasts: Map<string, { element: HTMLElement; timer?: number }> = new Map();

  constructor(options: ToastContainerOptions = {}) {
    this.options = {
      position: options.position || 'bottom-right',
      maxToasts: options.maxToasts || 5,
      defaultDuration: options.defaultDuration || 5000,
    };

    this.container = this.createContainer();
    document.body.appendChild(this.container);
  }

  /**
   * Create the toast container element
   */
  private createContainer(): HTMLElement {
    // Check if container already exists
    let existing = document.getElementById('toast-container');
    if (existing) {
      return existing;
    }

    const container = document.createElement('div');
    container.id = 'toast-container';
    container.className = `fixed z-[200] flex flex-col gap-2 pointer-events-none ${POSITION_CLASSES[this.options.position]}`;
    container.setAttribute('aria-label', 'Notifications');
    container.setAttribute('role', 'region');

    return container;
  }

  /**
   * Show a toast notification
   */
  show(options: ToastOptions): string {
    const id = generateToastId();

    // Remove oldest toast if at max
    if (this.toasts.size >= this.options.maxToasts) {
      const oldestId = this.toasts.keys().next().value;
      if (oldestId) {
        this.dismiss(oldestId);
      }
    }

    const onDismiss = () => this.dismiss(id);
    const element = createToastElement(id, options, onDismiss);
    element.classList.add('pointer-events-auto');

    // Add to container
    if (this.options.position.startsWith('top')) {
      this.container.appendChild(element);
    } else {
      this.container.insertBefore(element, this.container.firstChild);
    }

    // Track the toast
    const toastData: { element: HTMLElement; timer?: number } = { element };
    this.toasts.set(id, toastData);

    // Animate in
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateX(0)';
    });

    // Set auto-dismiss timer
    const duration = options.duration !== undefined ? options.duration : this.options.defaultDuration;
    if (duration > 0) {
      toastData.timer = window.setTimeout(() => {
        this.dismiss(id);
        options.onDismiss?.();
      }, duration);
    }

    return id;
  }

  /**
   * Dismiss a toast by ID
   */
  dismiss(id: string): void {
    const toastData = this.toasts.get(id);
    if (!toastData) return;

    const { element, timer } = toastData;

    // Clear timer
    if (timer) {
      clearTimeout(timer);
    }

    // Animate out
    element.style.opacity = '0';
    element.style.transform = 'translateX(100%)';

    // Remove after animation
    setTimeout(() => {
      element.remove();
      this.toasts.delete(id);
    }, 300);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    for (const id of this.toasts.keys()) {
      this.dismiss(id);
    }
  }

  /**
   * Shorthand methods for common toast types
   */
  success(message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>): string {
    return this.show({ ...options, message, type: 'success' });
  }

  error(message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>): string {
    return this.show({ ...options, message, type: 'error', duration: options?.duration ?? 0 });
  }

  warning(message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>): string {
    return this.show({ ...options, message, type: 'warning' });
  }

  info(message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>): string {
    return this.show({ ...options, message, type: 'info' });
  }

  /**
   * Show a loading toast that can be updated later
   */
  loading(message: string): { id: string; update: (options: Partial<ToastOptions>) => void; dismiss: () => void } {
    const id = this.show({
      message,
      type: 'default',
      icon: 'loader',
      duration: 0,
      dismissible: false,
    });

    // Add spinning animation to the loader icon
    const toastData = this.toasts.get(id);
    if (toastData) {
      const icon = toastData.element.querySelector('.lucide-icon');
      if (icon) {
        icon.classList.add('animate-spin');
      }
    }

    return {
      id,
      update: (options: Partial<ToastOptions>) => {
        const toastData = this.toasts.get(id);
        if (!toastData) return;

        // Update the toast content
        this.dismiss(id);
        this.show({
          message,
          ...options,
          duration: options.duration ?? 3000,
        });
      },
      dismiss: () => this.dismiss(id),
    };
  }

  /**
   * Update position
   */
  setPosition(position: ToastPosition): void {
    this.options.position = position;
    this.container.className = `fixed z-[200] flex flex-col gap-2 pointer-events-none ${POSITION_CLASSES[position]}`;
  }

  /**
   * Destroy the container
   */
  destroy(): void {
    this.dismissAll();
    this.container.remove();
  }
}

// Global toast instance
let globalToastContainer: ToastContainer | null = null;

/**
 * Get or create the global toast container
 */
export function getToastContainer(options?: ToastContainerOptions): ToastContainer {
  if (!globalToastContainer) {
    globalToastContainer = new ToastContainer(options);
  }
  return globalToastContainer;
}

/**
 * Reset the global toast container (for testing purposes)
 */
export function resetToastContainer(): void {
  if (globalToastContainer) {
    globalToastContainer.destroy();
    globalToastContainer = null;
  }
}

/**
 * Convenience function to show a toast using the global container
 */
export function toast(options: ToastOptions | string): string {
  const container = getToastContainer();
  if (typeof options === 'string') {
    return container.show({ message: options });
  }
  return container.show(options);
}

/**
 * Convenience shorthand functions
 */
export const toastSuccess = (message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>) =>
  getToastContainer().success(message, options);

export const toastError = (message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>) =>
  getToastContainer().error(message, options);

export const toastWarning = (message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>) =>
  getToastContainer().warning(message, options);

export const toastInfo = (message: string, options?: Partial<Omit<ToastOptions, 'message' | 'type'>>) =>
  getToastContainer().info(message, options);

export const toastLoading = (message: string) =>
  getToastContainer().loading(message);

export const dismissToast = (id: string) =>
  getToastContainer().dismiss(id);

export const dismissAllToasts = () =>
  getToastContainer().dismissAll();

/**
 * Show a toast notification (convenience wrapper)
 * @param messageOrOptions - Either a message string or ToastOptions object
 * @param type - Toast type (when first arg is a string)
 */
export function showToast(
  messageOrOptions: string | ToastOptions,
  type?: ToastType
): string {
  if (typeof messageOrOptions === 'string') {
    return toast({ message: messageOrOptions, type: type ?? 'info' });
  }
  return toast(messageOrOptions);
}

export default ToastContainer;
